import { SurveyEvent, SurveyEventEmailTemplateEnum, SurveyEventSubmit } from "../models/survey.event.model";
import api from "../lib/api";
import { Pagination, TableApiRes } from "../models/base.model";
import { deleteConfirmDialog } from "./common.action";
import { Stack, Typography } from "@mui/material";
import { Question } from "../models/question.model";

export async function getSurveyEvents(): Promise<TableApiRes<SurveyEvent>> {
  const data = (
    await api<{ surveyEvents: SurveyEvent[] }>({
      url: `/survey-events`,
    })
  ).surveyEvents;
  return { data, total: data.length };
}

export async function getSurveyEventSubmitsById({
  id,
  ...rest
}: {
  id: string;
} & Pagination): Promise<{ data: SurveyEventSubmit[]; offset?: any; total?: number }> {
  const data = await api<{
    surveyEventSubmits: SurveyEventSubmit[];
    offset?: any;
    total?: number;
  }>({
    method: "POST",
    url: `/survey-events/${id}/submits/query`,
    data: rest,
  });
  return {
    data: data.surveyEventSubmits,
    offset: data?.offset,
    ...(rest.needTotal && { total: data?.total }),
  };
}

export async function getSubmitAnswers(param: { eventId: string; submitId: string }): Promise<{ answers: any; questions: Question[] }> {
  const data = await api<{ answers: any; questions: Question[] }>({
    url: `/survey-events/submits/answers`,
    data: param,
    method: "POST",
  });
  return { answers: data.answers, questions: data.questions || [] };
}

export async function getOneSurveyEventSubmitById(eventId: string, submitId: string): Promise<SurveyEventSubmit> {
  const data = (
    await api<{ surveyEventSubmit: SurveyEventSubmit }>({
      url: `/survey-events/${eventId}/submits/${submitId}`,
    })
  ).surveyEventSubmit;
  return data;
}

export async function addSurveyEvent(param: SurveyEvent): Promise<SurveyEvent> {
  const data = (
    await api<{ surveyEvent: SurveyEvent }>({
      url: `/survey-events`,
      data: param,
      method: "POST",
      message: "Survey event created successfully",
    })
  ).surveyEvent;
  return data;
}

export async function updateSurveyEvent(param: Partial<SurveyEvent>) {
  const data = (
    await api<{ surveyEvent: SurveyEvent }>({
      url: `/survey-events/${param.id}`,
      data: param,
      method: "PATCH",
    })
  ).surveyEvent;
  return data;
}

export async function deleteSurveyEvent(id: string) {
  await deleteConfirmDialog(() =>
    api({
      url: `/survey-events/${id}`,
      method: "DELETE",
    })
  );
}

export async function sendSurveyEventEmails(param: {
  id: string;
  customerEmails: { email: string; template: SurveyEventEmailTemplateEnum }[];
}) {
  if (param.customerEmails.length === 0) {
    window.webApp.showMessage("warning", "No available email data found.");
    return;
  }
  const data = await api<{ surveyEvent: SurveyEvent; errors: string[] }>({
    url: `/survey-events/emails`,
    data: param,
    method: "POST",
  });
  window.webApp.toggleDialog({
    title: "Send Emails To Customers Results",
    okText: "OK",
    noCancel: true,
    element: (
      <div>
        <Typography variant="h6" gutterBottom>
          Successfully send email count: {param.customerEmails.length - data.errors.length}
        </Typography>
        {data.errors.length > 0 && (
          <Stack direction="column" spacing={1}>
            <Typography variant="body1">Here are some errors when sending email</Typography>
            {data.errors.map((e) => (
              <Typography key={e} variant="subtitle1">
                {e}
              </Typography>
            ))}
          </Stack>
        )}
      </div>
    ),
    open: true,
    onOk: async () => {},
  });
  return [];
}

export async function deleteSurveyEventSubmit(surveyEventSubmitId: string, companyId: string) {
  await deleteConfirmDialog(() =>
    api({
      url: `/survey-event-submits/${surveyEventSubmitId}`,
      method: "DELETE",
      data: {
        sk: companyId,
      },
    })
  );
}

export async function getAllRecordsUrl(props: { id: string; companyId: string }) {
  const data = (
    await api<{
      url: string;
    }>({
      url: `/survey-events/${props.id}/submits/download`,
      method: "POST",
      data: props,
    })
  ).url;
  return data;
}
